<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import DataFetcher from '@/features/data/presentation/components/DataFetcher.vue'
import FileUploadWidget from '@/presentation/components/input/FileUploadWidget.vue'
import { EVENTS } from '@/shared/constants/commons'
import Events from '@/events/events'
import Commons from '@/shared/utils/commons'

const props = defineProps({
  restaurantId: { type: String, required: true },
  restaurantName: { type: String, default: '' },
  initialData: { type: Object, default: null }
})

const emit = defineEmits(['close', 'success'])

const isEditing = computed(() => !!props.initialData?.id)
const saveTriggerRef = ref(null)
const isLoading = ref(false)
const step = ref(1) // 1: General, 2: Options, 3: Accompaniments

const defaultForm = () => ({
  id: '',
  title: '',
  description: '',
  price: 0,
  category: '',
  categoryId: '',
  subcategory: '',
  subcategoryId: '',
  image: '',
  images: [],
  options: [],
  accompaniments: [],
  cookDurationInMin: 15,
  storeId: props.restaurantId,
  storeName: props.restaurantName || ''
})

const form = ref(defaultForm())

onMounted(() => {
  if (props.initialData) {
    const d = props.initialData
    form.value = {
      id: d.id || '',
      title: d.title || d.name || '',
      description: d.description || '',
      price: d.price || 0,
      category: d.category || '',
      categoryId: d.categoryId || '',
      subcategory: d.subcategory || '',
      subcategoryId: d.subcategoryId || '',
      image: d.principalImageUrl || d.image || '',
      images: Array.isArray(d.images) ? [...d.images] : [],
      options: Array.isArray(d.options) ? JSON.parse(JSON.stringify(d.options)) : [],
      accompaniments: Array.isArray(d.accompaniments) ? [...d.accompaniments] : [],
      cookDurationInMin: d.cookDurationInMin || 15,
      storeId: props.restaurantId || d.storeId,
      storeName: d.storeName || props.restaurantName || ''
    }
  }
})

const categories = ref([])

const onCategoriesFetched = (res) => {
  const data = res?.data || res || {}
  const items = data?.result || (Array.isArray(data) ? data : [])
  categories.value = items
}

const subcategories = computed(() => {
  if (!Array.isArray(categories.value)) return []
  const cat = categories.value.find(c => c.name === form.value.category || c.id === form.value.categoryId)
  return cat?.subcategories || []
})

watch(() => form.value.category, (newVal) => {
  if (!Array.isArray(categories.value)) return
  const cat = categories.value.find(c => c.name === newVal)
  if (cat) {
    form.value.categoryId = cat.id
    // Reset subcategory if it's not in the new category
    if (!cat.subcategories?.find(s => s.name === form.value.subcategory)) {
      form.value.subcategory = ''
      form.value.subcategoryId = ''
    }
  }
})

watch(() => form.value.subcategory, (newVal) => {
  const sub = subcategories.value.find(s => s.name === newVal)
  if (sub) form.value.subcategoryId = sub.id
})

// Handle image uploads
const handleImageSaved = (data) => {
  if (data?.files?.length > 0) {
    // Current FileUploadWidget implementation usually gives one image if maxFiles is 1
    // or multiple if maxFiles > 1.
    // Let's assume the first one is principal.
    form.value.image = data.files[0].url
    if (data.files.length > 1) {
      form.value.images = data.files.slice(1).map(f => f.url)
    }
  }
}

const nextStep = () => {
  if (step.value === 1) {
    if (!form.value.title?.trim()) {
      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Validation', body: 'Le titre est requis', type: 'error' })
      return
    }
    if (!form.value.price) {
      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Validation', body: 'Le prix est requis', type: 'error' })
      return
    }
  }
  step.value++
}

const prevStep = () => {
  step.value--
}

const addOption = () => {
  form.value.options.push({
    id: Commons.generateUUID(),
    name: '',
    required: true,
    maxChoices: 1,
    choices: [
      { id: Commons.generateUUID(), name: '', amount: 0, max: 1 },
      { id: Commons.generateUUID(), name: '', amount: 0, max: 1 }
    ]
  })
}

const removeOption = (index) => {
  form.value.options.splice(index, 1)
}

const addChoice = (optionIndex) => {
  form.value.options[optionIndex].choices.push({
    id: Commons.generateUUID(),
    name: '',
    amount: 0,
    max: 1
  })
}

const removeChoice = (optionIndex, choiceIndex) => {
  form.value.options[optionIndex].choices.splice(choiceIndex, 1)
}

const submit = () => {
  isLoading.value = true
  const payload = { 
    ...form.value,
    storeId: props.restaurantId || form.value.storeId,
    storeName: props.restaurantName || form.value.storeName,
    action: isEditing.value ? 'update' : 'create' 
  }
  saveTriggerRef.value?.trigger(payload)
}

const onSuccess = () => {
  isLoading.value = false
  emit('success')
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Succès', body: 'Le plat a été enregistré avec succès.', type: 'success' })
}

const onError = (err) => {
  isLoading.value = false
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, { title: 'Erreur', body: err?.message || 'Erreur lors de l\'enregistrement', type: 'error' })
}
</script>

<template>
  <div class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" @click.self="$emit('close')">
    <div class="bg-white w-full max-w-2xl rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
        <div>
          <h2 class="text-lg font-bold text-gray-900">{{ isEditing ? 'Modifier le plat' : 'Ajouter un plat' }}</h2>
          <div class="flex items-center gap-2 mt-1">
            <span class="w-2 h-2 rounded-full" :class="step >= 1 ? 'bg-orange-500' : 'bg-gray-200'"></span>
            <span class="w-2 h-2 rounded-full" :class="step >= 2 ? 'bg-orange-500' : 'bg-gray-200'"></span>
            <span class="w-2 h-2 rounded-full" :class="step >= 3 ? 'bg-orange-500' : 'bg-gray-200'"></span>
            <span class="text-[10px] text-gray-500 uppercase font-bold ml-2">Étape {{ step }} sur 3</span>
          </div>
        </div>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100">
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-6 custom-scrollbar">
        
        <!-- Step 1: General Info -->
        <div v-if="step === 1" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Image du plat</label>
            <FileUploadWidget
              :defaultUrls="form.image ? [form.image, ...form.images] : []"
              :maxFiles="5"
              :acceptedFiles="['image/jpeg', 'image/png', 'image/webp']"
              @saved="handleImageSaved"
            />
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1">Nom du plat <span class="text-rose-500">*</span></label>
              <input v-model="form.title" type="text" placeholder="Ex: Pizza Margherita" class="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none" />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Prix (FCFA) <span class="text-rose-500">*</span></label>
              <input v-model.number="form.price" type="number" class="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none" />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Temps de prép. (min)</label>
              <input v-model.number="form.cookDurationInMin" type="number" class="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none" />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea v-model="form.description" rows="3" placeholder="Ingrédients, portion, etc..." class="w-full rounded-xl border border-gray-300 p-4 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none resize-none"></textarea>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
              <select v-model="form.category" class="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none bg-white">
                <option value="">Sélectionner</option>
                <option v-for="cat in categories" :key="cat.id" :value="cat.name">{{ cat.name }}</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Sous-catégorie</label>
              <select v-model="form.subcategory" class="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none bg-white">
                <option value="">Sélectionner</option>
                <option v-for="sub in subcategories" :key="sub.id" :value="sub.name">{{ sub.name }}</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Step 2: Options -->
        <div v-if="step === 2" class="space-y-6">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="font-bold text-gray-900">Options du plat</h3>
              <p class="text-xs text-gray-500">Ex: Suppléments, Taille, Cuisson...</p>
            </div>
            <button @click="addOption" class="px-3 py-1.5 bg-orange-50 text-orange-600 rounded-lg text-xs font-bold hover:bg-orange-100 transition-colors flex items-center gap-1">
              <span>+ Ajouter une option</span>
            </button>
          </div>

          <div v-if="form.options.length === 0" class="py-12 border-2 border-dashed border-gray-100 rounded-2xl flex flex-col items-center justify-center text-gray-400">
            <svg class="w-12 h-12 mb-2 opacity-20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
            <span class="text-sm">Aucune option configurée</span>
          </div>

          <div v-for="(option, idx) in form.options" :key="option.id" class="bg-gray-50 rounded-2xl p-5 border border-gray-200 space-y-4">
            <div class="flex items-start justify-between gap-4">
              <div class="flex-1">
                <input v-model="option.name" type="text" placeholder="Nom de l'option (ex: Choix de sauce)" class="w-full h-10 border-b border-gray-300 bg-transparent focus:border-orange-500 outline-none text-sm font-bold" />
              </div>
              <button @click="removeOption(idx)" class="text-rose-400 hover:text-rose-600 p-1">
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </button>
            </div>

            <div class="flex items-center gap-6">
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" v-model="option.required" class="w-4 h-4 accent-orange-500 rounded" />
                <span class="text-xs font-medium text-gray-700">Obligatoire</span>
              </label>
              <div class="flex items-center gap-2">
                <span class="text-xs text-gray-500">Max choix :</span>
                <input v-model.number="option.maxChoices" type="number" min="1" class="w-12 h-7 rounded border border-gray-300 text-center text-xs outline-none" />
              </div>
            </div>

            <div class="space-y-2 pt-2">
              <div v-for="(choice, cIdx) in option.choices" :key="choice.id" class="flex items-center gap-3">
                <input v-model="choice.name" type="text" placeholder="Choix" class="flex-1 h-9 rounded-lg border border-gray-300 px-3 text-xs outline-none focus:border-orange-500" />
                <div class="flex items-center gap-2 bg-white rounded-lg border border-gray-300 px-2 h-9">
                  <input v-model.number="choice.amount" type="number" placeholder="0" class="w-14 text-right text-xs outline-none border-none bg-transparent" />
                  <span class="text-[10px] text-gray-400 font-bold">XOF</span>
                </div>
                <button @click="removeChoice(idx, cIdx)" v-show="option.choices.length > 2" class="text-gray-300 hover:text-rose-500">
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <button @click="addChoice(idx)" class="text-[10px] font-bold text-orange-600 hover:underline mt-1">+ Ajouter un choix</button>
            </div>
          </div>
        </div>

        <!-- Step 3: Accompaniments -->
        <div v-if="step === 3" class="space-y-6">
          <div>
            <h3 class="font-bold text-gray-900">Accompagnements suggérés</h3>
            <p class="text-xs text-gray-500 mb-4">Séléctionnez les accompagnements qui seront proposés avec ce plat.</p>
          </div>

          <DataFetcher dataKey="accompaniements" v-slot="{ data: accData, loading }">
            <div v-if="loading" class="grid grid-cols-2 gap-3">
              <div v-for="i in 4" :key="i" class="h-16 bg-gray-50 animate-pulse rounded-xl"></div>
            </div>
            <div v-else class="grid grid-cols-2 gap-3">
              <label 
                v-for="acc in (Array.isArray(accData) ? accData : (accData?.result || []))" 
                :key="acc.id" 
                class="flex items-center gap-3 p-3 rounded-xl border border-gray-100 cursor-pointer transition-all"
                :class="form.accompaniments.includes(acc.id) ? 'bg-orange-50 border-orange-200' : 'hover:bg-gray-50'"
              >
                <input type="checkbox" :value="acc.id" v-model="form.accompaniments" class="w-4 h-4 accent-orange-500 rounded" />
                <div class="flex-1">
                  <div class="text-xs font-bold text-gray-800">{{ acc.name }}</div>
                  <div class="text-[10px] text-gray-500">{{ acc.amount }} FCFA</div>
                </div>
              </label>
            </div>
          </DataFetcher>
        </div>

      </div>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/50">
        <button v-if="step === 1" @click="$emit('close')" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50">
          Annuler
        </button>
        <button v-else @click="prevStep" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50">
          Précédent
        </button>

        <div class="flex items-center gap-3">
          <button v-if="step < 3" @click="nextStep" class="px-6 py-2 text-sm font-medium text-white bg-orange-600 rounded-xl hover:bg-orange-700 shadow-sm transition-all">
            Suivant
          </button>
          <button v-else @click="submit" :disabled="isLoading" class="px-8 py-2 text-sm font-medium text-white bg-green-600 rounded-xl hover:bg-green-700 shadow-sm disabled:opacity-60 transition-all flex items-center gap-2">
            <span v-if="isLoading" class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
            {{ isEditing ? 'Mettre à jour' : 'Enregistrer' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Hidden Fetchers -->
    <div class="hidden">
      <DataFetcher dataKey="categories" @fetched="onCategoriesFetched" :filters="{ limit: 100 }" />
    </div>
    
    <!-- Mutation Triggers -->
    <ActionTrigger
      dataKey="saveDish"
      ref="saveTriggerRef"
      @success="onSuccess"
      @failed="onError"
    />
  </div>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #d1d5db;
}
</style>
